import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Trailer, TrailerRequest } from '~models/trailer.model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CreateUpdateTrailerResponse, TrailerService } from '~services/trailer.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TrailerType } from '~v2Models/well.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { Subcarrier } from '~models/subcarrier.model';

@Component({
  selector: 'sa-trailer-management-create-edit',
  templateUrl: './trailer-management-create-edit.component.html',
  styleUrls: ['./trailer-management-create-edit.component.scss'],
})
export class TrailerManagementCreateEditComponent implements OnInit {
  @Input() public editTrailer: Trailer;
  @Input() public trailerTypes: Observable<TrailerType[]>;
  @Input() public subcarriers: Observable<Subcarrier[]>;
  @Output() public onCloseClicked: EventEmitter<boolean> = new EventEmitter<boolean>();

  public networkActive$$ = new BehaviorSubject<boolean>(false);

  public trailerForm: FormGroup;
  public isEdit: boolean;
  public regions: string[] = [
    'South Texas',
    'East Texas',
    'West Texas',
    'Northeast',
    'Oklahoma',
    'Colorado',
    'North Dakota',
    'Wyoming',
    'None',
  ];

  public statuses = [
    { key: 'available', name: 'Available' },
    { key: 'rented', name: 'Rented' },
    { key: 'maintenance', name: 'Maintenance' },
    { key: 'out_of_service', name: 'Out of Service' },
  ];

  constructor(private trailerService: TrailerService, private fb: FormBuilder, private snackbar: MatSnackBar) {}

  ngOnInit(): void {
    this.isEdit = !!this.editTrailer?.trailerId;

    if (this.isEdit) {
      this.setupEditForm();
    } else {
      this.setupNewForm();
    }
  }

  closeClicked(close) {
    this.onCloseClicked.emit(close);
  }

  private async setupNewForm() {
    this.trailerForm = this.fb.group({
      trailerName: ['', [Validators.required]],
      vin: [''],
      trailerTypeId: ['', [Validators.required]],
      trailerRegion: ['', [Validators.required]],
      trailerStatus: ['', [Validators.required]],
      subcarrierId: ['', [Validators.required]],
    });
  }

  private async setupEditForm() {
    const t = this.editTrailer;
    this.trailerForm = this.fb.group({
      trailerName: [t.trailerName, [Validators.required]],
      vin: [t.vin],
      trailerTypeId: [t.trailerTypeId, [Validators.required]],
      trailerRegion: [t.trailerRegion, [Validators.required]],
      trailerStatus: [t.trailerStatus, [Validators.required]],
      subcarrierId: [t.subcarrierId, [Validators.required]],
    });
  }

  public async createOrUpdateTrailer() {
    try {
      this.networkActive$$.next(true);

      if (this.trailerForm.invalid) {
        this.networkActive$$.next(false);
        return;
      }

      const formValue = this.trailerForm.getRawValue() as TrailerRequest;
      const body: TrailerRequest = {
        trailerName: formValue.trailerName,
        trailerRegion: formValue.trailerRegion,
        vin: formValue.vin,
        trailerStatus: formValue.trailerStatus,
        subcarrierId: formValue.subcarrierId === 0 ? null : formValue.subcarrierId,
        trailerTypeId: formValue.trailerTypeId,
      };

      let result: CreateUpdateTrailerResponse;
      if (this.isEdit) {
        result = await this.trailerService.updateTrailer(this.editTrailer.trailerId, body);
      } else {
        result = await this.trailerService.createTrailer(body);
      }

      if (result.Success) {
        const message = this.isEdit ? 'Trailer was successfully updated' : 'Trailer was successfully created';
        this.snackbar.open(message, null, { duration: 3000 });
        this.closeClicked(true);
      } else {
        const message = result.Error;
        this.snackbar.open(message, null, { duration: 3000, panelClass: ['snackbar-error'] });
      }
    } finally {
      this.networkActive$$.next(false);
    }
  }
}
